import React from 'react';

import dayjs from '~/timeInit';

import { Box, ButtonBase, Chip, Divider, Paper, Stack, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { HeatmapSurchargeRoomNight } from '~/types/heatmapSurcharge';

import { HeatmapCalendarDateStatus } from './approval';
import { formatCount, formatMonth, getForecast, getMonthCells } from './utils';

interface Props {
  month: string;
  roomTypeName: string;
  roomNights: Array<HeatmapSurchargeRoomNight>;
  dateStatuses: ReadonlyMap<string, HeatmapCalendarDateStatus>;
  bulkSelectedDates: ReadonlySet<string>;
  selectedDate?: string;
  onSelectDate: (date: string) => void;
  onToggleBulkSelectedDate: (date: string) => void;
  children?: React.ReactNode;
}

const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const statusLabels: Record<HeatmapCalendarDateStatus, string> = {
  ready: 'Ready',
  target_met: 'Target met',
  pending: 'Submitted',
  applied: 'Applied',
  needs_review: 'Review',
};

export default function HeatmapCalendar({
  month,
  roomTypeName,
  roomNights,
  dateStatuses,
  bulkSelectedDates,
  selectedDate,
  onSelectDate,
  onToggleBulkSelectedDate,
  children,
}: Props) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  // Availability colors must stay distinct from the portal's action-button colors.
  const colors = {
    low: theme.palette.luxPlus.dark,
    medium: alpha(theme.palette.luxPlus.main, isDarkMode ? 0.35 : 0.2),
    high: alpha(theme.palette.success.main, isDarkMode ? 0.3 : 0.18),
    selected: isDarkMode ? theme.palette.luxPlus.light : theme.palette.luxPlus.main,
  };
  const statusStyles: Record<HeatmapCalendarDateStatus, { backgroundColor: string; color: string }> = {
    ready: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    },
    target_met: {
      backgroundColor: isDarkMode ? theme.palette.grey[200] : theme.palette.common.white,
      color: theme.palette.common.black,
    },
    pending: {
      backgroundColor: theme.palette.info.main,
      color: theme.palette.info.contrastText,
    },
    applied: {
      backgroundColor: theme.palette.success.main,
      color: theme.palette.success.contrastText,
    },
    needs_review: {
      backgroundColor: theme.palette.warning.main,
      color: theme.palette.warning.contrastText,
    },
  };
  const nightsByDate = new Map(roomNights.map((night) => [night.travelDate, night]));
  const selectedNight = nightsByDate.get(selectedDate ?? '');
  const selectedForecast = selectedNight && getForecast(selectedNight);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6">Heatmap reference</Typography>
          <Typography variant="body2" color="text.secondary">
            {roomTypeName}
          </Typography>
        </Box>
        <Box textAlign="center">
          <Typography variant="h6">{formatMonth(month)}</Typography>
          <Typography variant="body2" color="text.secondary">
            Predicted rooms left by check-in date
          </Typography>
        </Box>
        <Box aria-label={`${formatMonth(month)} forecast calendar`}>
          <Box display="grid" gridTemplateColumns="repeat(7, minmax(0, 1fr))" gap={0.375}>
            {weekDays.map((day) => (
              <Typography key={day} align="center" variant="caption">
                {day}
              </Typography>
            ))}
            {getMonthCells(month).map((date, index) => {
              if (!date) return <Box key={`empty-${index}`} />;
              const roomNight = nightsByDate.get(date);
              const forecast = roomNight && getForecast(roomNight);
              const dateStatus = dateStatuses.get(date);
              const isBulkSelected = bulkSelectedDates.has(date);
              const color = !forecast
                ? theme.palette.action.disabledBackground
                : forecast.roomsLeft < 5
                  ? colors.low
                  : forecast.roomsLeft < 11
                    ? colors.medium
                    : colors.high;
              return (
                <Box
                  key={date}
                  data-no-dark-override
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    minHeight: 76,
                    px: 0.25,
                    py: 0.75,
                    borderRadius: 1,
                    bgcolor: color,
                    backgroundImage:
                      selectedDate === date
                        ? `linear-gradient(${alpha(colors.selected, isDarkMode ? 0.24 : 0.1)}, ${alpha(
                            colors.selected,
                            isDarkMode ? 0.24 : 0.1,
                          )})`
                        : undefined,
                    color: !forecast
                      ? theme.palette.text.secondary
                      : forecast.roomsLeft < 5
                        ? theme.palette.common.white
                        : theme.palette.text.primary,
                    boxShadow: isBulkSelected
                      ? `inset 0 0 0 2px ${theme.palette.common.white}, inset 0 0 0 4px ${colors.selected}`
                      : selectedDate === date
                        ? `inset 0 0 0 2px ${colors.selected}`
                        : dateStatus === 'ready'
                          ? `inset 0 0 0 2px ${theme.palette.primary.main}`
                          : undefined,
                  }}
                >
                  <ButtonBase
                    disabled={!roomNight}
                    aria-pressed={selectedDate === date}
                    aria-label={`${dayjs(date).format('D MMMM YYYY')}: ${
                      forecast ? `${formatCount(forecast.roomsLeft)} predicted rooms left` : 'No forecast available'
                    }${dateStatus ? `, ${statusLabels[dateStatus]}` : ''}${
                      isBulkSelected ? ', selected for approval' : ''
                    }`}
                    onClick={() => onSelectDate(date)}
                    sx={{
                      flexGrow: 1,
                      flexDirection: 'column',
                      alignItems: 'stretch',
                      color: 'inherit',
                      '&:focus-visible': { outline: '3px solid', outlineColor: colors.selected },
                    }}
                  >
                    <Typography variant="caption" align="left" color="inherit" sx={{ pl: 0.5 }}>
                      {dayjs(date).date()}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" color="inherit">
                      {forecast ? formatCount(forecast.roomsLeft) : '—'}
                    </Typography>
                    <Typography variant="caption" color="inherit">
                      {forecast ? 'left' : 'No data'}
                    </Typography>
                  </ButtonBase>
                  {dateStatus && (
                    <Chip
                      size="small"
                      label={
                        isBulkSelected
                          ? 'Selected'
                          : dateStatus === 'ready'
                            ? `+ ${statusLabels[dateStatus]}`
                            : statusLabels[dateStatus]
                      }
                      clickable={dateStatus === 'ready'}
                      aria-pressed={dateStatus === 'ready' ? isBulkSelected : undefined}
                      aria-label={
                        dateStatus === 'ready'
                          ? `${isBulkSelected ? 'Remove' : 'Select'} ${dayjs(date).format(
                              'D MMMM YYYY',
                            )} for bulk approval`
                          : undefined
                      }
                      onClick={
                        dateStatus === 'ready'
                          ? () => {
                              onSelectDate(date);
                              onToggleBulkSelectedDate(date);
                            }
                          : undefined
                      }
                      sx={{
                        alignSelf: 'center',
                        maxWidth: '100%',
                        height: 16,
                        mt: 0.25,
                        ...statusStyles[dateStatus],
                        color:
                          dateStatus === 'target_met'
                            ? `${theme.palette.common.black} !important`
                            : statusStyles[dateStatus].color,
                        border: '1px solid',
                        borderColor:
                          dateStatus === 'target_met' && isDarkMode
                            ? theme.palette.grey[500]
                            : alpha(theme.palette.common.black, 0.45),
                        '& .MuiChip-label': {
                          minWidth: 0,
                          px: 0.5,
                          fontSize: '0.5625rem',
                          lineHeight: 1,
                          textAlign: 'center',
                          color:
                            dateStatus === 'target_met'
                              ? `${theme.palette.common.black} !important`
                              : `${statusStyles[dateStatus].color} !important`,
                        },
                      }}
                    />
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap aria-label="Predicted rooms left legend">
          <Chip size="small" label="< 5 left" sx={{ bgcolor: colors.low, color: theme.palette.common.white }} />
          <Chip size="small" label="5–10 left" sx={{ bgcolor: colors.medium, color: 'text.primary' }} />
          <Chip size="small" label="11+ left" sx={{ bgcolor: colors.high, color: 'text.primary' }} />
          <Chip size="small" label="No data" />
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap aria-label="Surcharge status legend">
          <Typography variant="caption" alignSelf="center">
            Surcharge status:
          </Typography>
          {Object.entries(statusLabels).map(([status, label]) => (
            <Chip key={status} size="small" label={label} sx={statusStyles[status as HeatmapCalendarDateStatus]} />
          ))}
        </Stack>
        <Divider />
        {selectedNight && (
          <Box aria-live="polite">
            <Typography fontWeight="bold">{dayjs(selectedNight.travelDate).format('D MMM YYYY')}</Typography>
            <Typography variant="body2">
              {selectedForecast
                ? `Forecast calculation: ${formatCount(selectedNight.bookedAtGeneration)} booked at forecast + ${formatCount(
                    selectedNight.predictedAdditionalBookings,
                  )} additional bookings predicted = ${formatCount(selectedForecast.finalBookings)} predicted final bookings.`
                : 'Forecast inventory is unavailable for this date.'}
            </Typography>
            {selectedForecast && (
              <Typography variant="body2" color="text.secondary">
                Allocation: {formatCount(selectedNight.allocatedAtGeneration)} rooms · Forecast availability:{' '}
                {formatCount(selectedForecast.roomsLeft)} rooms left. These are forecast values, not live inventory.
              </Typography>
            )}
            {!!selectedForecast?.exceedsAllocationBy && (
              <Typography variant="body2" color="warning.main">
                Forecast exceeds allocation by {formatCount(selectedForecast.exceedsAllocationBy)} booking
                {selectedForecast.exceedsAllocationBy === 1 ? '' : 's'}, so availability is displayed as 0 rather than
                negative.
              </Typography>
            )}
          </Box>
        )}
        <Typography variant="body2" color="text.secondary">
          Select a date or recommendation to compare its forecast.
        </Typography>
        {children}
      </Stack>
    </Paper>
  );
}
